import { SimpleUserCo2QuizAnswer } from '@/models/user.model';
import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from './base.service';
import {
  Co2BasketValue,
  Co2CountryAverage,
  Co2QuizQuestion,
  Co2QuizResults,
} from '@/models/co2-quiz.model';
import {
  getAuthorizationHeader,
  getCMSHeader,
  getLanguageHeader,
} from '@/services/ajax';

class Co2QuizQuestionsService extends BaseService {
  private url: string = '/api/co2-quiz-question';
  private valueUrl: string = '/api/co2-basket-value';
  private averageUrl: string = '/api/co2-country-average';
  private resultsUrl: string = '/api/co2-results';

  constructor() {
    super();
  }

  public async get(id: number): Promise<DataResponse<Co2QuizQuestion>> {
    const res = await this.$http.get<Co2QuizQuestion>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getList(): Promise<DataResponse<Co2QuizQuestion[]>> {
    const res = await this.$http.get<Co2QuizQuestion[]>(`${this.url}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async create(
    item: Partial<Co2QuizQuestion>,
  ): Promise<DataResponse<Co2QuizQuestion>> {
    const res = await this.$http.post<Co2QuizQuestion>(this.url, item);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async edit(
    item: Co2QuizQuestion,
  ): Promise<DataResponse<Co2QuizQuestion>> {
    const res = await this.$http.put<Co2QuizQuestion>(
      `${this.url}/${item.id}`,
      item,
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getValues(): Promise<DataResponse<Co2BasketValue[]>> {
    const res = await this.$http.get<Co2BasketValue[]>(`${this.valueUrl}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }
  public async getValue(id: number): Promise<DataResponse<Co2BasketValue>> {
    const res = await this.$http.get<Co2BasketValue>(`${this.valueUrl}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async createValue(
    item: Partial<Co2BasketValue>,
  ): Promise<DataResponse<Co2BasketValue>> {
    const res = await this.$http.post<Co2BasketValue>(this.valueUrl, item);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async editValue(
    item: Co2BasketValue,
  ): Promise<DataResponse<Co2BasketValue>> {
    const res = await this.$http.put<Co2BasketValue>(
      `${this.valueUrl}/${item.id}`,
      item,
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getAverages(): Promise<DataResponse<Co2CountryAverage[]>> {
    const res = await this.$http.get<Co2CountryAverage[]>(`${this.averageUrl}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }
  public async getAverage(
    id: number,
  ): Promise<DataResponse<Co2CountryAverage>> {
    const res = await this.$http.get<Co2CountryAverage>(
      `${this.averageUrl}/${id}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async createAverage(
    item: Partial<Co2CountryAverage>,
  ): Promise<DataResponse<Co2CountryAverage>> {
    const res = await this.$http.post<Co2CountryAverage>(this.averageUrl, item);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async editAverage(
    item: Co2CountryAverage,
  ): Promise<DataResponse<Co2CountryAverage>> {
    const res = await this.$http.put<Co2CountryAverage>(
      `${this.averageUrl}/${item.id}`,
      item,
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async updateOrder(
    order: number[],
  ): Promise<DataResponse<Co2QuizQuestion[]>> {
    const res = await this.$http.put<Co2QuizQuestion[]>(
      `${this.url}/order`,
      order.map((id, index) => ({ id, index })),
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getResults(
    answers: SimpleUserCo2QuizAnswer[],
  ): Promise<DataResponse<Co2QuizResults>> {
    const request = new Request(`${this.resultsUrl}`, {
      method: 'POST',
      body: JSON.stringify(answers),
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeader(),
        ...getCMSHeader(),
        ...getLanguageHeader(),
        appVersion: `1.4.0`,
      },
    });
    const res = await this.$http.postComplex<Co2QuizResults>(request);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }
}

export default new Co2QuizQuestionsService();
